import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Container, Form, Button, Alert, Spinner, Row, Col, Badge } from 'react-bootstrap';
import './OrderForm.css';
import api from '../services/api';
import { showToast } from '../components/ToastNotification';
import SearchableSelect from '../components/SearchableSelect';
import OrderInvoiceModal from '../components/OrderInvoiceModal';
import { typDokladu } from '../components/invoiceActions';
import { confirm } from '../components/ConfirmDialog';
import { popisAdresy, serazeneAdresy, odpovidaAresu, aresJednimRadkem, TYP_SIDLO } from '../components/adresy';

// Stav storna — objednávka a položka ho mají zapsaný jinak (enum_booking_state vs. enum_booking_item_state).
const STAV_STORNO = 'CANCELED';
const STAV_POLOZKY_STORNO = 'Canceled';
/** `responsibility_type` — kontakt pro „Faktury a upomínky". */
const ODPOVEDNOST_FAKTURACE = 'Invoicing';
const NULOVE_CENY = { grossPrice: 0, netPrice: 0, netNetPrice: 0, netNetPricePerUnit: 0, totalPrice: 0 };

// Datum vydání projektu "2026-10-23T00:00:00+00:00" → "23.10.2026" (bez převodu časové zóny)
const formatProjectDate = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

// ============================================================
// PRICE CALCULATION (mirrors backend logic)
// ============================================================

/**
 * Apply a single discount/surcharge to a running price.
 * If the discount has a CZK amount (_czk), use that directly.
 * Otherwise use the percentage.
 */
function applyDiscount(runningPrice, disc) {
  const czkAmount = parseFloat(disc._czk);
  if (!isNaN(czkAmount) && czkAmount !== 0 && disc._czk !== '' && disc._czk !== undefined) {
    // Absolute CZK amount — add directly (negative = discount, positive = surcharge)
    return runningPrice + czkAmount;
  }
  const pct = parseFloat(disc.percentage) || 0;
  if (pct === 0) return runningPrice;
  return runningPrice * (1 + pct / 100);
}

/**
 * Calculate prices for a single booking item given its discounts.
 * Percentages are in human-readable form: 10 = +10%, -5 = -5%.
 * CZK amounts (_czk) are applied as absolute values directly.
 */
function calculateItemPrices(basicPrice, quantity, discounts, vatPercentage) {
  const sorted = [...discounts].sort((a, b) => (a.step_id || 0) - (b.step_id || 0));

  // Surcharges (steps 1-3)
  let grossPrice = basicPrice;
  sorted.forEach(d => {
    const step = d.step_id || 0;
    if (step >= 1 && step <= 3) {
      grossPrice = applyDiscount(grossPrice, d);
    }
  });

  // Discounts (steps 4-10)
  let netPrice = grossPrice;
  sorted.forEach(d => {
    const step = d.step_id || 0;
    if (step >= 4 && step <= 10) {
      netPrice = applyDiscount(netPrice, d);
    }
  });

  // Agency commission (step 11)
  let netNetPrice = netPrice;
  sorted.forEach(d => {
    const step = d.step_id || 0;
    if (step === 11) {
      netNetPrice = applyDiscount(netNetPrice, d);
    }
  });

  // VAT
  const vatAmount = netNetPrice * (vatPercentage / 100);
  const totalPrice = netNetPrice + vatAmount;

  const qty = quantity > 0 ? quantity : 1;
  const netNetPricePerUnit = netNetPrice / qty;

  return {
    grossPrice: Math.round(grossPrice * 100) / 100,
    netPrice: Math.round(netPrice * 100) / 100,
    netNetPrice: Math.round(netNetPrice * 100) / 100,
    netNetPricePerUnit: Math.round(netNetPricePerUnit * 100) / 100,
    totalPrice: Math.round(totalPrice * 100) / 100,
  };
}

/**
 * Format price for Czech locale
 */
function formatPrice(value) {
  if (value === null || value === undefined || value === '') return '—';
  const num = parseFloat(value);
  if (isNaN(num)) return '—';
  return num.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Parse price_per_unit string like "35 000,00 CZK" → 35000.00
 */
function parsePricePerUnit(str) {
  if (!str) return 0;
  // Remove currency suffix and whitespace, replace comma with dot
  const cleaned = String(str).replace(/[A-Za-z]/g, '').replace(/\s/g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

/**
 * Povaha řádku podle `step_id`: slevy a agenturní provize (4–11) cenu vždy
 * snižují, takže se u nich znaménko doplňuje. U přirážek (1–3) se nehlídá —
 * v datech z Contractu je 73 řádků „Přirážka −60 %", kterými se zadávala
 * sleva, a otočit je na plus by cenu objednávky zvedlo. Ostatní kroky
 * (Náklady, DPH, …) do výpočtu ceny nevstupují vůbec.
 * @returns {number} -1 = vždy záporné, 0 = nehlídá se
 */
function znamenkoKroku(stepId) {
  const step = Number(stepId) || 0;
  if (step >= 4 && step <= 11) return -1;
  return 0;
}

/**
 * Obchodník napíše do slevy „10" a myslí tím deset procent dolů. Znaménko
 * proto doplňujeme podle typu řádku, ať ho nemusí psát — a ať ho nemůže
 * splést. Hodnota se přepisuje jen když znaménko nesedí, jinak by se při
 * psaní ztrácela rozepsaná desetinná místa („10,50" → „10,5").
 */
function srovnejZnamenko(value, stepId) {
  const znamenko = znamenkoKroku(stepId);
  if (!znamenko || value === '' || value === null || value === undefined) return value;
  const cislo = parseFloat(value);
  if (isNaN(cislo) || cislo === 0) return value;
  if (Math.sign(cislo) === znamenko) return value;
  return String(Math.abs(cislo) * znamenko);
}

/**
 * Hlášky k řádku slevy, když zadaná hodnota nedává smysl.
 * @returns {string|null}
 */
function varovaniSlevy(disc) {
  if (!znamenkoKroku(disc.step_id)) return null;
  const pct = parseFloat(disc.percentage);
  if (!isNaN(pct) && pct <= -100) {
    return 'Sleva 100 % a víc — cena spadne na nulu nebo do mínusu.';
  }
  return null;
}

// Default discount template for a new item
function createDefaultDiscounts() {
  return [
    { discount_type_id: 1, step_id: 1, percentage: '', direct: 0, _czk: '' },  // Přirážka
    { discount_type_id: 4, step_id: 4, percentage: '', direct: 0, _czk: '' },  // Mimořádná sleva
    { discount_type_id: 11, step_id: 11, percentage: '', direct: 0, _czk: '' }, // Agenturní provize
  ];
}

// Create a blank item
function createBlankItem() {
  return {
    medium_id: '',
    project_id: '',
    mutation_id: '',
    ratecard_id: '',
    ratecard_item_id: '',
    location: '',
    state: 'Pending',
    note: '',
    quantity: 1,
    basic_price: '',
    vat_rate_id: 1,
    fixed_price: false,
    discounts: createDefaultDiscounts(),
    // Loaded data
    _projects: [],
    _mutations: [],
    _ratecards: [],
    _ratecardItems: [],
    _formatLabel: '',
    _ratecardItemPrice: 0,
  };
}


// ============================================================
// COMPANY AUTOCOMPLETE SUB-COMPONENT
// ============================================================

function CompanyAutocomplete({ label, value, displayName, onChange, placeholder = 'Hledat firmu…' }) {
  const [search, setSearch] = useState(displayName || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync display when external value changes
  useEffect(() => {
    setSearch(displayName || '');
  }, [displayName]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSearch = (val) => {
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (val.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await api.searchCompanies(val, 15);
        setResults(result.data || []);
        setShowDropdown(true);
      } catch (err) {
        console.error('Company search failed:', err);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleSelect = (company) => {
    setSearch(company.company_name);
    setShowDropdown(false);
    onChange(company);
  };

  const handleClear = () => {
    setSearch('');
    setResults([]);
    setShowDropdown(false);
    onChange(null);
  };

  return (
    <div className="company-autocomplete-wrapper" ref={wrapperRef}>
      <Form.Label>{label}</Form.Label>
      <div className="position-relative">
        <Form.Control
          type="text"
          placeholder={placeholder}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => { if (results.length > 0) setShowDropdown(true); }}
        />
        {value && (
          <Button
            variant="link"
            size="sm"
            className="position-absolute top-50 end-0 translate-middle-y text-muted"
            style={{ zIndex: 5 }}
            onClick={handleClear}
            title="Vymazat"
          >
            <i className="bi bi-x-lg"></i>
          </Button>
        )}
      </div>
      {showDropdown && (
        <div className="autocomplete-dropdown">
          {loading && <div className="autocomplete-loading">Hledám…</div>}
          {!loading && results.length === 0 && search.length >= 2 && (
            <div className="autocomplete-no-results">Žádné výsledky</div>
          )}
          {!loading && results.map(c => (
            <div key={c.id} className="autocomplete-item" onClick={() => handleSelect(c)}>
              <div className="company-name">{c.company_name}</div>
              {c.ico && <div className="company-ico">IČO: {c.ico}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * Ceny jedné položky pro zobrazení i pro součet objednávky.
 * Stornovaná položka se nefakturuje, takže všude vychází na 0 Kč — stejně to
 * ukládá backend (`OrderRepository::prepareItemRow`).
 */
function cenyPolozky(item, vatRates) {
  if (item.state === STAV_POLOZKY_STORNO) return NULOVE_CENY;
  const vatRate = vatRates.find(v => v.id === parseInt(item.vat_rate_id));
  return calculateItemPrices(
    parseFloat(item.basic_price) || 0,
    parseFloat(item.quantity) || 1,
    item.discounts,
    vatRate ? vatRate.percentage : 0,
  );
}


// ============================================================
// ORDER ITEM SUB-COMPONENT
// ============================================================

function OrderItemCard({
  item,
  index,
  enums,
  onUpdate,
  onRemove,
  onDuplicate,
  mediumOptions,
  objednavkaStornovana,
  mena,
  kurz,
}) {
  const { discount_types = [], vat_rates = [], booking_item_states = [] } = enums;

  // Collapsed state — items are collapsed by default, duplicated items start expanded
  const [collapsed, setCollapsed] = useState(!item._isDuplicate);

  // Track whether this is the initial mount (to avoid clearing pre-loaded values in edit mode)
  const isInitialMountRef = useRef(true);
  const prevMediumIdRef = useRef(item.medium_id);
  const prevRatecardIdRef = useRef(item.ratecard_id);
  const prevRatecardItemIdRef = useRef(item.ratecard_item_id);

  // Load projects when medium changes
  useEffect(() => {
    const isUserChange = !isInitialMountRef.current && prevMediumIdRef.current !== item.medium_id;
    prevMediumIdRef.current = item.medium_id;

    if (!item.medium_id) {
      if (isUserChange) {
        onUpdate(index, { _projects: [], project_id: '', _mutations: [], mutation_id: '', _ratecards: [], ratecard_id: '', _ratecardItems: [], ratecard_item_id: '', _formatLabel: '', _ratecardItemPrice: 0 });
      }
      return;
    }

    // Skip loading if data is already pre-loaded (edit mode initial mount)
    if (isInitialMountRef.current && item._projects.length > 0) {
      return;
    }

    const load = async () => {
      try {
        const [projRes, mutRes, rcRes] = await Promise.all([
          api.getMediumProjects(item.medium_id),
          api.getMediumMutations(item.medium_id),
          api.getMediumRatecards(item.medium_id, true),
        ]);
        const updates = {
          _projects: projRes.data || [],
          _mutations: mutRes.data || [],
          _ratecards: rcRes.data || [],
        };
        // Only clear dependent values if user changed the medium (not initial load)
        if (isUserChange) {
          updates.project_id = '';
          updates.mutation_id = '';
          updates.ratecard_id = '';
          updates._ratecardItems = [];
          updates.ratecard_item_id = '';
          updates._formatLabel = '';
          updates._ratecardItemPrice = 0;
        }
        onUpdate(index, updates);
      } catch (err) {
        console.error('Failed to load medium sub-data:', err);
      }
    };
    load();
  }, [item.medium_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load ratecard items when ratecard changes
  useEffect(() => {
    const isUserChange = !isInitialMountRef.current && prevRatecardIdRef.current !== item.ratecard_id;
    prevRatecardIdRef.current = item.ratecard_id;

    if (!item.ratecard_id) {
      if (isUserChange) {
        onUpdate(index, { _ratecardItems: [], ratecard_item_id: '', _formatLabel: '', _ratecardItemPrice: 0 });
      }
      return;
    }

    // Skip loading if data is already pre-loaded (edit mode initial mount)
    if (isInitialMountRef.current && item._ratecardItems.length > 0) {
      return;
    }

    const load = async () => {
      try {
        const res = await api.getRatecardItems(item.ratecard_id);
        const updates = {
          _ratecardItems: res.data?.items || [],
        };
        if (isUserChange) {
          updates.ratecard_item_id = '';
          updates._formatLabel = '';
          updates._ratecardItemPrice = 0;
        }
        onUpdate(index, updates);
      } catch (err) {
        console.error('Failed to load ratecard items:', err);
      }
    };
    load();
  }, [item.ratecard_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // When ratecard_item changes, load format and price
  useEffect(() => {
    const isUserChange = !isInitialMountRef.current && prevRatecardItemIdRef.current !== item.ratecard_item_id;
    prevRatecardItemIdRef.current = item.ratecard_item_id;

    if (!item.ratecard_item_id || !item._ratecardItems) return;
    const rcItem = item._ratecardItems.find(ri => String(ri.id) === String(item.ratecard_item_id));
    if (rcItem) {
      const price = parsePricePerUnit(rcItem.price_per_unit);
      const updates = {
        _formatLabel: rcItem.format_label || '',
        _ratecardItemPrice: price,
      };
      // Only override basic_price if user changed the ratecard item (not initial load)
      if (isUserChange && !item.fixed_price) {
        updates.basic_price = price;
      }
      onUpdate(index, updates);
    }
  }, [item.ratecard_item_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark initial mount as complete after first render cycle
  useEffect(() => {
    // Use a timeout to ensure all initial effects have run
    const timer = setTimeout(() => {
      isInitialMountRef.current = false;
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleFieldChange = (field, value) => {
    onUpdate(index, { [field]: value });
  };

  const handleDiscountChange = (discIdx, field, value) => {
    const newDiscounts = [...item.discounts];
    const stepId = newDiscounts[discIdx].step_id;
    // U procent i korun doplní znaménko podle typu řádku — sleva jde vždy dolů,
    // přirážka nahoru. Ostatní pole (název vlastní slevy) necháváme být.
    const hodnota = (field === 'percentage' || field === '_czk')
      ? srovnejZnamenko(value, stepId)
      : value;
    newDiscounts[discIdx] = { ...newDiscounts[discIdx], [field]: hodnota };

    // When CZK amount is entered, clear the percentage (CZK takes priority in calculation)
    if (field === '_czk') {
      newDiscounts[discIdx].percentage = '';
      newDiscounts[discIdx].direct = 1;
    }

    // When percentage is entered, clear the CZK amount
    if (field === 'percentage') {
      newDiscounts[discIdx].direct = 0;
      newDiscounts[discIdx]._czk = '';
    }

    // Hodnotu jsme srovnali, takže případné varování o znaménku z načtení
    // už neplatí.
    delete newDiscounts[discIdx]._znamenkoOpraveno;

    onUpdate(index, { discounts: newDiscounts });
  };

  const handleAddDiscount = (discountTypeId) => {
    if (!discountTypeId) return;

    // Custom discount
    if (discountTypeId === 'custom') {
      const newDiscounts = [...item.discounts, {
        discount_type_id: null,
        step_id: 5,
        percentage: '',
        direct: 0,
        _czk: '',
        _isCustom: true,
        name: '',
      }];
      newDiscounts.sort((a, b) => (a.step_id || 0) - (b.step_id || 0));
      onUpdate(index, { discounts: newDiscounts });
      return;
    }

    const dt = discount_types.find(d => d.id === parseInt(discountTypeId));
    if (!dt) return;
    // Check if already exists
    if (item.discounts.some(d => d.discount_type_id === dt.id)) return;
    const newDiscounts = [...item.discounts, {
      discount_type_id: dt.id,
      step_id: dt.sort_order,
      percentage: '',
      direct: 0,
      _czk: '',
    }];
    // Sort by step_id
    newDiscounts.sort((a, b) => (a.step_id || 0) - (b.step_id || 0));
    onUpdate(index, { discounts: newDiscounts });
  };

  const handleRemoveDiscount = (discIdx) => {
    const newDiscounts = item.discounts.filter((_, i) => i !== discIdx);
    onUpdate(index, { discounts: newDiscounts });
  };

  // Calculate prices
  const prices = cenyPolozky(item, vat_rates);

  // Kolik řádků mělo při načtení obrácené znaménko (data z Contractu).
  const opraveneZnamenko = item.discounts.filter(d => d._znamenkoOpraveno).length;

  // Available discount types not yet added
  const usedDiscountTypeIds = item.discounts.map(d => d.discount_type_id);
  const availableDiscountTypes = discount_types.filter(dt => !usedDiscountTypeIds.includes(dt.id));

  // Medium options for SearchableSelect
  const mediumSelectOptions = mediumOptions.map(m => ({ id: m.id, label: m.name }));

  // Build display title for collapsed state
  const selectedRcItem = item.ratecard_item_id
    ? (item._ratecardItems || []).find(ri => String(ri.id) === String(item.ratecard_item_id))
    : null;
  const itemTitle = selectedRcItem
    ? `${selectedRcItem.name}${item._formatLabel ? ' — ' + item._formatLabel : ''}`
    : 'Nová položka';

  // Faktury, na kterých položka už je (stav se načítá spolu s objednávkou).
  const invoiced = item._invoices || [];

  return (
    <div className={`order-item-card${collapsed ? ' is-collapsed' : ''}`}>
      <div className="item-header" onClick={() => setCollapsed(prev => !prev)} style={{ cursor: 'pointer' }}>
        <h6>
          <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-down'} me-2`}></i>
          <span className="item-index">Položka {item._displayNumber || item.number || `#${index + 1}`}</span>
          {item.number && (
            item.state === 'Canceled'
              ? <Badge bg="light" text="dark" className="ms-2 border">Storno</Badge>
              : invoiced.length > 0
                ? (
                  <Badge
                    bg="success"
                    className="ms-2"
                    title={`Položka je na dokladech: ${invoiced.map(i => `${typDokladu(i.type)} ${i.number || i.id}`).join(', ')}`}
                  >
                    <i className="bi bi-receipt me-1"></i>
                    {invoiced.map(i => `${typDokladu(i.type)} ${i.number || i.id}`).join(' · ')}
                  </Badge>
                )
                : <Badge bg="secondary" className="ms-2"><i className="bi bi-hourglass-split me-1"></i>Nefakturováno</Badge>
          )}
          {collapsed && <span className="item-title-preview">{itemTitle}</span>}
        </h6>
        <div className="d-flex gap-1">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onDuplicate(index); }}
            title="Duplikovat položku"
          >
            <i className="bi bi-copy"></i>
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onRemove(index); }}
            title="Odebrat položku"
          >
            <i className="bi bi-trash"></i>
          </Button>
        </div>
      </div>
      {!collapsed && <div className="item-body">
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Form.Label>Médium</Form.Label>
            <SearchableSelect
              options={mediumSelectOptions}
              value={item.medium_id}
              onChange={(val) => handleFieldChange('medium_id', val)}
              placeholder="Hledat médium…"
              emptyLabel="— vyberte —"
              allowEmpty={false}
            />
          </Col>
          <Col md={4}>
            <Form.Label>Projekt</Form.Label>
            <Form.Select
              value={item.project_id}
              onChange={(e) => handleFieldChange('project_id', e.target.value)}
              disabled={!item.medium_id || item._projects.length === 0}
            >
              <option value="">— vyberte —</option>
              {(item._projects || []).map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}{formatProjectDate(p.date) ? ` (${formatProjectDate(p.date)})` : ''}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Label>Mutace</Form.Label>
            <Form.Select
              value={item.mutation_id || ''}
              onChange={(e) => handleFieldChange('mutation_id', e.target.value)}
              disabled={!item.medium_id || (item._mutations || []).length === 0}
            >
              <option value="">— bez mutace —</option>
              {(item._mutations || []).map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col md={4}>
            <Form.Label>Ceník</Form.Label>
            <Form.Select
              value={item.ratecard_id}
              onChange={(e) => handleFieldChange('ratecard_id', e.target.value)}
              disabled={!item.medium_id || item._ratecards.length === 0}
            >
              <option value="">— vyberte —</option>
              {(item._ratecards || []).map(r => (
                <option key={r.id} value={r.id}>
                  {r.name}{r.is_available ? '' : ' (neaktivní)'}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Label>Položka ceníku</Form.Label>
            <Form.Select
              value={item.ratecard_item_id}
              onChange={(e) => handleFieldChange('ratecard_item_id', e.target.value)}
              disabled={!item.ratecard_id || item._ratecardItems.length === 0}
            >
              <option value="">— vyberte —</option>
              {(item._ratecardItems || []).map(ri => (
                <option key={ri.id} value={ri.id}>
                  {ri.name}{ri.format_label ? ` (${ri.format_label})` : ''}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Label>Umístění</Form.Label>
            <Form.Control
              type="text"
              value={item.location}
              onChange={(e) => handleFieldChange('location', e.target.value)}
              placeholder="např. Strana 5"
            />
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col md={4}>
            <Form.Label>Stav</Form.Label>
            <Form.Select
              value={item.state}
              onChange={(e) => handleFieldChange('state', e.target.value)}
              disabled={objednavkaStornovana}
            >
              {booking_item_states.map(s => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </Form.Select>
            {objednavkaStornovana && (
              <Form.Text className="text-muted">
                Objednávka je ve stornu, takže i všechny její položky.
              </Form.Text>
            )}
          </Col>
          <Col md={8}>
            <Form.Label>Poznámka</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={item.note}
              onChange={(e) => handleFieldChange('note', e.target.value)}
            />
          </Col>
        </Row>

        {/* Price calculation section */}
        <div className="price-section">
          <Row className="g-3 mb-3">
            <Col md={3}>
              <Form.Label>Rozměry</Form.Label>
              <Form.Control type="text" value={item._formatLabel || '—'} readOnly className="bg-light" />
            </Col>
            <Col md={2}>
              <Form.Label>Množství</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={item.quantity}
                readOnly
                className="bg-light"
              />
            </Col>
            <Col md={3}>
              <Form.Label>Základní cena</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={item.basic_price}
                onChange={(e) => handleFieldChange('basic_price', e.target.value)}
                readOnly={!item.fixed_price}
                className={!item.fixed_price ? 'bg-light' : ''}
              />
            </Col>
            <Col md={2}>
              <Form.Label>Fixní cena</Form.Label>
              <Form.Check
                type="checkbox"
                label="Přepsat"
                checked={item.fixed_price}
                onChange={(e) => {
                  const checked = e.target.checked;
                  handleFieldChange('fixed_price', checked);
                  if (!checked) {
                    // Revert to ratecard price
                    handleFieldChange('basic_price', item._ratecardItemPrice || 0);
                  }
                }}
              />
            </Col>
            <Col md={2}>
              <Form.Label>DPH</Form.Label>
              <Form.Select
                value={item.vat_rate_id}
                onChange={(e) => handleFieldChange('vat_rate_id', e.target.value)}
              >
                {vat_rates.map(v => (
                  <option key={v.id} value={v.id}>{v.label}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          {/* Discount rows */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong style={{ fontSize: '.9rem' }}>Slevy a přirážky</strong>
              <div className="d-flex align-items-center gap-2">
                <Form.Select
                  size="sm"
                  style={{ maxWidth: 220 }}
                  id={`add-discount-${index}`}
                  defaultValue=""
                  onChange={(e) => { handleAddDiscount(e.target.value); e.target.value = ''; }}
                >
                  <option value="">+ Přidat…</option>
                  {availableDiscountTypes.map(dt => (
                    <option key={dt.id} value={dt.id}>{dt.label}</option>
                  ))}
                  <option value="custom">+ vlastní</option>
                </Form.Select>
              </div>
            </div>

            {item.discounts.map((disc, discIdx) => {
              const isCustom = disc._isCustom || (!disc.discount_type_id && disc.name !== undefined);
              const dtLabel = isCustom
                ? (disc.name || 'Vlastní sleva')
                : (discount_types.find(dt => dt.id === disc.discount_type_id)?.label || `Typ ${disc.discount_type_id}`);
              const isDefault = [1, 4, 11].includes(disc.discount_type_id);
              const napoveda = znamenkoKroku(disc.step_id)
                ? 'Sleva — mínus se doplní sám, stačí napsat 10'
                : 'Kladná hodnota cenu zvyšuje, záporná snižuje';
              const varovani = varovaniSlevy(disc);
              return (
                <div key={discIdx} className="discount-row">
                  {isCustom ? (
                    <Form.Control
                      type="text"
                      placeholder="Název slevy…"
                      value={disc.name || ''}
                      onChange={(e) => handleDiscountChange(discIdx, 'name', e.target.value)}
                      style={{ minWidth: 160, maxWidth: 180 }}
                      size="sm"
                    />
                  ) : (
                    <span className="discount-label">{dtLabel}</span>
                  )}
                  <div className="discount-inputs">
                    <Form.Control
                      type="number"
                      step="0.01"
                      placeholder="%"
                      title={napoveda}
                      value={disc.percentage}
                      onChange={(e) => handleDiscountChange(discIdx, 'percentage', e.target.value)}
                    />
                    <span className="input-unit">%</span>
                    <Form.Control
                      type="number"
                      step="0.01"
                      placeholder="CZK"
                      title={napoveda}
                      value={disc._czk || ''}
                      onChange={(e) => handleDiscountChange(discIdx, '_czk', e.target.value)}
                    />
                    <span className="input-unit">CZK</span>
                    {(!isDefault || isCustom) && (
                      <Button variant="outline-danger" size="sm" onClick={() => handleRemoveDiscount(discIdx)} title="Odebrat">
                        <i className="bi bi-x"></i>
                      </Button>
                    )}
                  </div>
                  {varovani && (
                    <div className="discount-warning">
                      <i className="bi bi-exclamation-triangle"></i> {varovani}
                    </div>
                  )}
                </div>
              );
            })}
            {opraveneZnamenko > 0 && (
              <div className="discount-warning mt-2">
                <i className="bi bi-exclamation-triangle"></i>{' '}
                {opraveneZnamenko === 1
                  ? 'U jedné slevy jsme srovnali znaménko podle typu řádku — zkontrolujte cenu.'
                  : `U ${opraveneZnamenko} slev jsme srovnali znaménko podle typu řádku — zkontrolujte cenu.`}
              </div>
            )}
          </div>

          {/* Price breakdown — ceny se zadávají v korunách (ceník je v korunách),
              u objednávky v cizí měně se vedle konečných částek ukáže přepočet. */}
          <div className="price-row">
            <span className="price-label">Hrubá cena (Gross)</span>
            <span className="price-value">{formatPrice(prices.grossPrice)} CZK</span>
          </div>
          <div className="price-row">
            <span className="price-label">Čistá cena (Net)</span>
            <span className="price-value">{formatPrice(prices.netPrice)} CZK</span>
          </div>
          <div className="price-row">
            <span className="price-label">Net Net cena</span>
            <span className="price-value">
              {formatPrice(prices.netNetPrice)} CZK
              {kurz > 0 && (
                <span className="price-foreign"> ≈ {formatPrice(prices.netNetPrice / kurz)} {mena}</span>
              )}
            </span>
          </div>
          <div className="price-row">
            <span className="price-label">Net Net / ks</span>
            <span className="price-value">{formatPrice(prices.netNetPricePerUnit)} CZK</span>
          </div>
          <div className="price-row total">
            <span className="price-label">Celkem s DPH</span>
            <span className="price-value">
              {formatPrice(prices.totalPrice)} CZK
              {kurz > 0 && (
                <span className="price-foreign"> ≈ {formatPrice(prices.totalPrice / kurz)} {mena}</span>
              )}
            </span>
          </div>
          {kurz > 0 && (
            <div className="price-row">
              <span className="price-label text-muted">Kurz</span>
              <span className="price-value text-muted">1 {mena} = {formatPrice(kurz)} CZK</span>
            </div>
          )}
          {prices.netNetPrice < 0 && (
            <div className="discount-warning mt-2">
              <i className="bi bi-exclamation-triangle"></i> Výsledná cena položky je záporná — zkontrolujte slevy.
            </div>
          )}
        </div>
      </div>}
    </div>
  );
}


// ============================================================
// MAIN ORDER FORM COMPONENT
// ============================================================

function OrderForm({ openTab, closeTab, activeTab, orderId, initialCustomerId, initialCustomerName }) {
  const isEditMode = !!orderId;

  // Enums
  const [enums, setEnums] = useState({
    booking_states: [],
    payment_types: [],
    booking_item_states: [],
    discount_types: [],
    vat_rates: [],
  });

  // Media list for SearchableSelect
  const [allMedia, setAllMedia] = useState([]);

  // Latest EUR exchange rate for display in currency select
  const [latestEurRate, setLatestEurRate] = useState(null);

  // Header fields
  // Nová objednávka otevřená z karty zákazníka má zákazníka předvyplněného
  const [customerId, setCustomerId] = useState(orderId ? '' : (initialCustomerId || ''));
  const [customerName, setCustomerName] = useState(orderId ? '' : (initialCustomerName || ''));
  const [advertiserId, setAdvertiserId] = useState('');
  const [advertiserName, setAdvertiserName] = useState('');
  const [responsiblePersonId, setResponsiblePersonId] = useState('');
  const [responsiblePersonName, setResponsiblePersonName] = useState('');
  const [billingAddressId, setBillingAddressId] = useState('');
  const [shippingAddressId, setShippingAddressId] = useState('');
  // Komu poslat fakturu z téhle objednávky. Prázdné = fakturační kontakt firmy.
  const [invoiceContactPersonId, setInvoiceContactPersonId] = useState('');
  const [advertiserAddressId, setAdvertiserAddressId] = useState('');
  const [abroad, setAbroad] = useState(false);
  const [paymentType, setPaymentType] = useState('Wire');
  const [currency, setCurrency] = useState('CZK');
  const [exchangeRate, setExchangeRate] = useState('');
  // Nová objednávka je rovnou Potvrzení (rezervace se volí ručně)
  const [state, setState] = useState('CONFIRMED');
  const [dateOfCreation, setDateOfCreation] = useState(new Date().toISOString().split('T')[0]);
  const [endOfReservation, setEndOfReservation] = useState('');
  const [sourcesCheckContacts, setSourcesCheckContacts] = useState('');
  const [internalNote, setInternalNote] = useState('');

  // Dependent data
  const [customerContacts, setCustomerContacts] = useState([]);
  const [sourcesSuggestOpen, setSourcesSuggestOpen] = useState(false);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [advertiserAddresses, setAdvertiserAddresses] = useState([]);

  // Items
  const [items, setItems] = useState([createBlankItem()]);

  // UI state
  const [orderNumber, setOrderNumber] = useState('');
  // Číslo, pod kterým objednávku vede zákazník — velké firmy a úřady ho chtějí na faktuře.
  const [customerOrderNumber, setCustomerOrderNumber] = useState('');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadingData, setLoadingData] = useState(isEditMode);

  // Load enums and media list on mount
  useEffect(() => {
    const loadEnums = async () => {
      try {
        const [enumRes, mediaRes] = await Promise.all([
          api.getOrderEnums(),
          api.getMedia({ limit: 500, sort: 'name', order: 'asc' }),
        ]);
        setEnums(enumRes.data || {});
        setAllMedia(mediaRes.data || []);
      } catch (err) {
        console.error('Failed to load enums:', err);
        setError('Nepodařilo se načíst číselníky');
      }
    };
    loadEnums();

    // Load latest EUR exchange rate separately (non-blocking)
    const loadRates = async () => {
      try {
        const ratesRes = await api.getLatestExchangeRates();
        const rates = ratesRes.data?.rates || [];
        const eurRate = rates.find(r => r.currency_code === 'EUR');
        if (eurRate) {
          const ratePerUnit = parseFloat(eurRate.rate) / (eurRate.amount || 1);
          setLatestEurRate(ratePerUnit);
        }
      } catch (err) {
        console.error('Failed to load exchange rates:', err);
      }
    };
    loadRates();

    // Nová objednávka: obchodník se předvyplní přihlášeným uživatelem. Účty
    // a kontaktní osoby spolu nemají vazbu (účty se naimportovaly s přihlašovacím
    // jménem místo e-mailu), takže se párují přes e-mail a jméno — a když to
    // nevyjde jednoznačně, pole zůstane prázdné a vybere se ručně.
    if (!isEditMode) {
      const loadMe = async () => {
        try {
          const ja = (await api.getMyContactPerson()).data;
          if (!ja) return;
          // Ručně vybraného obchodníka nepřepisovat — odpověď může dorazit až po
          // tom, co ho uživatel stihne vybrat sám.
          setResponsiblePersonId((prev) => prev || ja.id);
          setResponsiblePersonName((prev) => prev || `${ja.last_name || ''} ${ja.first_name || ''}`.trim());
        } catch (err) {
          console.error('Nepodařilo se předvyplnit obchodníka:', err);
        }
      };
      loadMe();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load order data in edit mode
  useEffect(() => {
    if (!isEditMode) return;

    const loadOrder = async () => {
      setLoadingData(true);
      try {
        const res = await api.getOrder(orderId);
        const data = res.data;
        if (!data) {
          setError('Objednávka nenalezena');
          return;
        }

        // Header
        setOrderNumber(data.number || '');
        setCustomerOrderNumber(data.customer_order_number || '');
        setCustomerId(data.customer_id || '');
        setCustomerName(data.customer_name || '');
        setAdvertiserId(data.advertiser_id || '');
        setAdvertiserName(data.advertiser_name || '');
        setResponsiblePersonId(data.responsible_person_id || '');
        setResponsiblePersonName(data.responsible_person_name || '');
        setBillingAddressId(data.billing_address_id || '');
        setShippingAddressId(data.shipping_address_id || '');
        setAdvertiserAddressId(data.advertiser_address_id || '');
        setAbroad(!!data.abroad);
        setPaymentType(data.payment_type || 'Wire');
        setCurrency(data.currency || 'CZK');
        setExchangeRate(data.exchange_rate || '');
        setState(data.state || 'RESERVATION');
        setDateOfCreation(data.date_of_creation || '');
        // Convert "2014-04-07 07:19:01" → "2014-04-07T07:19" for datetime-local input
        if (data.end_of_reservation) {
          const eor = data.end_of_reservation.replace(' ', 'T').slice(0, 16);
          setEndOfReservation(eor);
        }
        setSourcesCheckContacts(data.sources_check_contacts || '');
        setInvoiceContactPersonId(data.invoice_contact_person_id || '');
        setInternalNote(data.internal_note || '');

        // Dependent data
        setCustomerAddresses(data.customer_addresses || []);
        setAdvertiserAddresses(data.advertiser_addresses || []);

        // Items
        if (data.items && data.items.length > 0) {
          const loadedItems = await Promise.all(data.items.map(async (bi) => {
            // Load sub-data for each item
            let projects = [], mutations = [], ratecards = [], ratecardItems = [];
            if (bi.medium_id) {
              try {
                const [pRes, mRes, rRes] = await Promise.all([
                  api.getMediumProjects(bi.medium_id),
                  api.getMediumMutations(bi.medium_id),
                  api.getMediumRatecards(bi.medium_id, true),
                ]);
                projects = pRes.data || [];
                mutations = mRes.data || [];
                ratecards = rRes.data || [];

                // Starší objednávky často míří na ceník, který je dnes neaktivní
                // (42 028 položek v datech). Kdyby ve výběru chyběl, select by se
                // tvářil jako prázdný a uložení by vazbu na ceník smazalo.
                if (bi.ratecard_id && !ratecards.some(r => r.id === bi.ratecard_id)) {
                  ratecards = [...ratecards, {
                    id: bi.ratecard_id,
                    name: bi.ratecard_name || `Ceník #${bi.ratecard_id}`,
                    is_available: 0,
                  }];
                }
              } catch (e) { /* ignore */ }
            }
            if (bi.ratecard_id) {
              try {
                const riRes = await api.getRatecardItems(bi.ratecard_id);
                ratecardItems = riRes.data?.items || [];
              } catch (e) { /* ignore */ }
            }

            // Map discounts from DB format to form format
            const discounts = (bi.discounts || []).map(d => {
              const puvodni = d.percentage !== null ? Math.round(d.percentage * 10000) / 100 : ''; // Convert from decimal fraction to %
              // Pár desítek řádků z Contractu má znaménko naopak (sleva s plusem,
              // přirážka s mínusem). Srovnáme ho hned při načtení, ať se na
              // obrazovce počítá to samé, co se po uložení zapíše — a položku
              // označíme, aby si toho obchodník všiml dřív, než uloží.
              const srovnane = srovnejZnamenko(puvodni, d.step_id || 0);
              return {
                discount_type_id: d.discount_type_id || null,
                step_id: d.step_id || 0,
                percentage: srovnane === '' ? '' : Number(srovnane),
                direct: d.direct || 0,
                _czk: '',
                name: d.name || undefined,
                _isCustom: !d.discount_type_id && !!d.name,
                _znamenkoOpraveno: (puvodni !== '' && Number(srovnane) !== Number(puvodni)) || undefined,
              };
            });

            // Ensure default discount types are present
            [1, 4, 11].forEach(dtId => {
              if (!discounts.some(d => d.discount_type_id === dtId)) {
                const dt = (enums.discount_types || []).find(t => t.id === dtId);
                discounts.push({
                  discount_type_id: dtId,
                  step_id: dt?.sort_order || dtId,
                  percentage: '',
                  direct: 0,
                  _czk: '',
                });
              }
            });
            discounts.sort((a, b) => (a.step_id || 0) - (b.step_id || 0));

            return {
              // id existující položky — backend podle něj položku upraví místo
              // smazání a založení znovu (jinak se ztratí vazba na fakturu)
              id: bi.id,
              number: bi.number || '',
              medium_id: bi.medium_id || '',
              project_id: bi.project_id || '',
              mutation_id: bi.mutation_id || '',
              ratecard_id: bi.ratecard_id || '',
              ratecard_item_id: bi.ratecard_item_id || '',
              location: bi.location || '',
              state: bi.state || 'Pending',
              note: bi.note || '',
              quantity: bi.quantity || 1,
              basic_price: bi.basic_price || '',
              vat_rate_id: bi.vat_rate_id || 1,
              fixed_price: false,
              discounts,
              _projects: projects,
              _mutations: mutations,
              _ratecards: ratecards,
              _ratecardItems: ratecardItems,
              _formatLabel: bi.format_label || '',
              _ratecardItemPrice: parsePricePerUnit(bi.ratecard_item_price_per_unit),
              // Faktury, na kterých položka je — stav se ukazuje v hlavičce položky.
              _invoices: bi.invoices || [],
            };
          }));
          setItems(loadedItems);
        }
      } catch (err) {
        console.error('Failed to load order:', err);
        setError('Nepodařilo se načíst objednávku');
      } finally {
        setLoadingData(false);
      }
    };
    loadOrder();
  }, [orderId, isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load customer addresses when customer changes.
  // Odpovědná osoba se na zákazníkovi nevěší — hledá se přes celou contact_person,
  // takže se při změně zákazníka nemaže.
  useEffect(() => {
    if (!customerId) {
      setCustomerAddresses([]);
      setCustomerContacts([]);
      setBillingAddressId('');
      setShippingAddressId('');
      setInvoiceContactPersonId('');
      return;
    }
    const load = async () => {
      try {
        const [addressesRes, contactsRes] = await Promise.all([
          api.getCompanyAddresses(customerId),
          api.getCompanyContacts(customerId),
        ]);
        setCustomerAddresses(addressesRes.data || []);
        setCustomerContacts((contactsRes.data || []).filter(c => c.email));
      } catch (err) {
        console.error('Failed to load customer sub-data:', err);
      }
    };
    load();
  }, [customerId]);

  // Návrhy pro „Kontakt pro kontrolu podkladů" — kontaktní osoby zákazníka;
  // osoby s odpovědností Podklady napřed, filtr podle rozepsaného textu.
  const sourcesSuggestions = useMemo(() => {
    const hledat = (sourcesCheckContacts || '').trim().toLowerCase();
    const seznam = customerContacts.filter(c => {
      if (!hledat || c.email?.toLowerCase() === hledat) return true;
      const text = `${c.email || ''} ${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
      return text.includes(hledat);
    });
    const maPodklady = (c) => (c.responsibilities || []).some(r => r.id === 'Sources');
    return [...seznam]
      .sort((a, b) => (maPodklady(b) ? 1 : 0) - (maPodklady(a) ? 1 : 0))
      .slice(0, 8);
  }, [customerContacts, sourcesCheckContacts]);

  // Kontakty pro výběr příjemce faktury. Lidi s odpovědností „Faktury a upomínky"
  // oddělujeme do vlastní skupiny — jsou to ti správní kandidáti, ale vybrat jde
  // kdokoli s e-mailem (zákazník s deseti lokalitami tu odpovědnost u všech mít nebude).
  const kontaktyProFakturu = useMemo(() => {
    const maFakturaci = (c) => (c.responsibilities || []).some(r => r.id === ODPOVEDNOST_FAKTURACE);
    const jmeno = (c) => [c.last_name, c.first_name].filter(Boolean).join(' ').trim() || c.email;
    const serad = (seznam) => [...seznam].sort((a, b) => jmeno(a).localeCompare(jmeno(b), 'cs'));
    return {
      fakturacni: serad(customerContacts.filter(maFakturaci)),
      ostatni: serad(customerContacts.filter(c => !maFakturaci(c))),
      jmeno,
    };
  }, [customerContacts]);

  // Sídlo patří nahoru — je to ta adresa, která má jít do fakturační.
  const adresyZakaznika = useMemo(() => serazeneAdresy(customerAddresses), [customerAddresses]);
  const adresyInzerenta = useMemo(() => serazeneAdresy(advertiserAddresses), [advertiserAddresses]);

  // ── Ověření sídla v ARESu ────────────────────────────────────────
  // Typ adresy v databázi říká, co jsme si kdysi uložili; ARES říká, co platí
  // dnes. Ptáme se na tlačítko, ne při každém otevření objednávky — většinu
  // času obchodník adresu vůbec neřeší.
  const [aresStav, setAresStav] = useState(null); // {hleda, chyba, adresa, shodaId}

  useEffect(() => { setAresStav(null); }, [customerId]);

  const overVAresu = async () => {
    setAresStav({ hleda: true });
    try {
      const firma = await api.getCompany(customerId);
      const ico = firma.data?.ico;
      if (!ico) {
        setAresStav({ chyba: 'Zákazník nemá vyplněné IČ, nemáme se podle čeho zeptat.' });
        return;
      }

      const res = await api.lookupAres(ico);
      const sidlo = res.data;
      const shoda = adresyZakaznika.find(a => odpovidaAresu(a, sidlo));
      setAresStav({ adresa: sidlo, shodaId: shoda ? shoda.id : null });

      // Když sídlo sedí a fakturační adresa ještě není vybraná, rovnou ji nabídneme.
      if (shoda && !billingAddressId) setBillingAddressId(String(shoda.id));
    } catch (err) {
      setAresStav({ chyba: err.message || 'ARES neodpověděl.' });
    }
  };

  const pridejAresAdresu = async () => {
    const sidlo = aresStav?.adresa;
    if (!sidlo) return;
    try {
      // Když zákazník nějaké sídlo uložené má (jen neodpovídá ARESu), neděláme
      // mu druhé — novou adresu založíme jako fakturační, protože přesně na to
      // ji tady potřebujeme. Staré sídlo zůstává, ať se nepřepíše ruční zápis.
      const maSidlo = customerAddresses.some(a => a.type === TYP_SIDLO);
      const res = await api.createAddress({
        company_id: customerId,
        type: maSidlo ? 'Billing' : TYP_SIDLO,
        street: sidlo.street || '',
        city: sidlo.city || '',
        postal_code: sidlo.postal_code || '',
        country_id: 2, // Česká republika — ARES jiné státy nevrací
        note: `Načteno z ARES (${new Date().toLocaleDateString('cs-CZ')})`,
      });
      const novaId = res.data?.id;
      const seznam = await api.getCompanyAddresses(customerId);
      setCustomerAddresses(seznam.data || []);
      if (novaId) {
        setBillingAddressId(String(novaId));
        setAresStav(prev => ({ ...prev, shodaId: novaId }));
      }
      showToast('Adresa sídla z ARESu přidána.', 'success');
    } catch (err) {
      setAresStav(prev => ({ ...prev, chyba: err.message || 'Adresu se nepodařilo uložit.' }));
    }
  };

  const aresShodaId = aresStav?.shodaId ?? null;
  const aresBlok = !customerId ? null : (
    <div className="mt-1">
      <Button
        variant="link"
        size="sm"
        className="p-0"
        onClick={overVAresu}
        disabled={aresStav?.hleda}
      >
        {aresStav?.hleda
          ? <><Spinner animation="border" size="sm" className="me-1" />Ověřuji v ARESu…</>
          : <><i className="bi bi-shield-check me-1"></i>Ověřit sídlo v ARESu</>}
      </Button>

      {aresStav?.chyba && (
        <div className="text-danger small mt-1">{aresStav.chyba}</div>
      )}

      {aresStav?.adresa && aresStav.shodaId && (
        <div className="text-success small mt-1">
          <i className="bi bi-check-circle me-1"></i>
          Sídlo podle ARESu sedí s označenou adresou.
        </div>
      )}

      {aresStav?.adresa && !aresStav.shodaId && (
        <div className="text-warning-emphasis small mt-1">
          <i className="bi bi-exclamation-triangle me-1"></i>
          Podle ARESu je sídlo <strong>{aresJednimRadkem(aresStav.adresa)}</strong> — žádná
          z uložených adres mu neodpovídá.
          <Button variant="link" size="sm" className="p-0 ms-1 align-baseline" onClick={pridejAresAdresu}>
            Přidat ji zákazníkovi
          </Button>
        </div>
      )}
    </div>
  );

  // Load advertiser addresses when advertiser changes
  useEffect(() => {
    if (!advertiserId) {
      setAdvertiserAddresses([]);
      setAdvertiserAddressId('');
      return;
    }
    const load = async () => {
      try {
        const res = await api.getCompanyAddresses(advertiserId);
        setAdvertiserAddresses(res.data || []);
      } catch (err) {
        console.error('Failed to load advertiser addresses:', err);
      }
    };
    load();
  }, [advertiserId]);

  // Fetch latest exchange rate when currency changes to non-CZK
  useEffect(() => {
    if (currency === 'CZK') {
      setExchangeRate('');
      return;
    }
    // Don't overwrite if already loaded from edit mode
    if (exchangeRate) return;

    const load = async () => {
      try {
        const res = await api.getLatestExchangeRates();
        const rates = res.data?.rates || [];
        const match = rates.find(r => r.currency_code === currency);
        if (match) {
          // rate is per `amount` units, e.g. 1 EUR = 25.xxx CZK
          const ratePerUnit = parseFloat(match.rate) / (match.amount || 1);
          setExchangeRate(ratePerUnit.toFixed(4));
        }
      } catch (err) {
        console.error('Failed to load exchange rate:', err);
      }
    };
    load();
  }, [currency]); // eslint-disable-line react-hooks/exhaustive-deps

  // Item management
  const handleItemUpdate = useCallback((index, updates) => {
    setItems(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], ...updates };
      return newItems;
    });
  }, []);

  const handleAddItem = () => {
    setItems(prev => [...prev, createBlankItem()]);
  };

  const handleDuplicateItem = (index) => {
    setItems(prev => {
      const source = prev[index];
      // Deep-clone the item (including discounts array)
      const clone = {
        ...source,
        id: undefined, // Duplikát je nová položka, nesmí přepsat zdrojový řádek
        number: '', // Clear DB number — duplicated item gets a new number on save
        discounts: source.discounts.map(d => ({ ...d })),
        _projects: [...source._projects],
        _mutations: [...(source._mutations || [])],
        _ratecards: [...source._ratecards],
        _ratecardItems: [...source._ratecardItems],
        _isDuplicate: true, // flag so the card opens expanded
      };
      const newItems = [...prev];
      newItems.splice(index + 1, 0, clone);
      return newItems;
    });
  };

  const handleRemoveItem = (index) => {
    setItems(prev => {
      if (prev.length <= 1) return prev; // Keep at least one item
      return prev.filter((_, i) => i !== index);
    });
  };

  // Calculate grand total
  const grandTotal = items.reduce((sum, item) => sum + cenyPolozky(item, enums.vat_rates).totalPrice, 0);

  // Stavy položek před stornem, ať se dají vrátit, když si to obchodník rozmyslí.
  const stavyPredStornem = useRef(null);

  /**
   * Storno celé objednávky znamená i storno všech jejích položek (ceny jdou na 0 Kč).
   * Ptáme se předem, protože se tím přepíšou stavy, které si někdo nastavil po položkách.
   */
  const zmenStavObjednavky = async (novyStav) => {
    if (novyStav === state) return;

    if (novyStav === STAV_STORNO) {
      // `_displayNumber` dolepuje až render, tady si číslo poskládáme stejně.
      const naFakture = items
        .map((i, idx) => ({ ...i, _cislo: i.number || (orderNumber ? `${orderNumber}/${String(idx + 1).padStart(2, '0')}` : '') }))
        .filter(i => (i._invoices || []).length > 0);
      const zprava = ['Vystornují se i všechny položky objednávky a jejich ceny se nastaví na 0 Kč.'];
      if (naFakture.length > 0) {
        const cisla = naFakture.map(i => i._cislo).filter(Boolean).join(', ');
        zprava.push(
          `Pozor: ${naFakture.length === 1 ? 'položka' : 'položky'} ${cisla} ${naFakture.length === 1 ? 'je' : 'jsou'} už na faktuře. `
          + 'Storno objednávky s vystaveným dokladem nic neudělá — ten se musí dobropisovat zvlášť.',
        );
      }
      const potvrzeno = await confirm({
        title: 'Stornovat celou objednávku?',
        message: zprava.join('\n\n'),
        confirmText: 'Stornovat objednávku',
        cancelText: 'Nechat být',
        variant: naFakture.length > 0 ? 'danger' : 'warning',
        icon: 'bi-exclamation-triangle',
      });
      if (!potvrzeno) return;

      stavyPredStornem.current = items.map(i => i.state || 'Pending');
      setItems(prev => prev.map(i => ({ ...i, state: STAV_POLOZKY_STORNO })));
    } else if (state === STAV_STORNO) {
      // Návrat ze storna: položky zpátky na původní stav, u načtené objednávky
      // (kde původní stav neznáme) na „Ve zpracování".
      const puvodni = stavyPredStornem.current;
      setItems(prev => prev.map((i, idx) => (
        i.state === STAV_POLOZKY_STORNO ? { ...i, state: puvodni?.[idx] || 'Pending' } : i
      )));
      stavyPredStornem.current = null;
    }

    setState(novyStav);
  };

  // Save handler
  const handleSave = async () => {
    setError('');

    // Bez kurzu nejde z objednávky v cizí měně vystavit fakturu — ceny jsou
    // v korunách a doklad by nesl korunové částky označené jako eura.
    if (currency !== 'CZK' && !(parseFloat(exchangeRate) > 0)) {
      setError(`Objednávka je v měně ${currency}, doplňte kurz — podle něj se přepočítá faktura.`);
      return;
    }

    // Bez obchodníka by faktura z téhle objednávky nikomu nepřinesla provizi —
    // počítají se po položkách a obchodník se tam propisuje odsud.
    if (!responsiblePersonId) {
      setError('Vyberte obchodníka — podle něj se počítají provize z faktur za tuhle objednávku.');
      return;
    }

    setSaving(true);

    try {
      // Build request body
      const body = {
        customer_id: customerId || null,
        advertiser_id: advertiserId || null,
        responsible_person_id: responsiblePersonId || null,
        billing_address_id: billingAddressId || null,
        shipping_address_id: shippingAddressId || null,
        advertiser_address_id: advertiserAddressId || null,
        abroad,
        customer_order_number: customerOrderNumber.trim() || null,
        payment_type: paymentType,
        currency,
        exchange_rate: currency !== 'CZK' && exchangeRate ? exchangeRate : null,
        state,
        date_of_creation: dateOfCreation,
        end_of_reservation: state === 'RESERVATION' && endOfReservation ? endOfReservation.replace('T', ' ') + ':00' : null,
        sources_check_contacts: sourcesCheckContacts,
        invoice_contact_person_id: invoiceContactPersonId || null,
        internal_note: internalNote,
        items: items.map(item => ({
          id: item.id || null,
          medium_id: item.medium_id || null,
          project_id: item.project_id || null,
          mutation_id: item.mutation_id || null,
          ratecard_id: item.ratecard_id || null,
          ratecard_item_id: item.ratecard_item_id || null,
          location: item.location || null,
          state: item.state || 'Pending',
          note: item.note || null,
          quantity: parseFloat(item.quantity) || 1,
          basic_price: parseFloat(item.basic_price) || 0,
          vat_rate_id: item.vat_rate_id ? parseInt(item.vat_rate_id) : null,
          discounts: item.discounts
            .filter(d => {
              const hasPct = d.percentage !== '' && d.percentage !== null && d.percentage !== undefined;
              const hasCzk = d._czk !== '' && d._czk !== null && d._czk !== undefined;
              return hasPct || hasCzk;
            })
            .map(d => ({
              discount_type_id: d.discount_type_id || null,
              percentage: parseFloat(d.percentage) || 0,
              czk_amount: parseFloat(d._czk) || 0,
              direct: d.direct || 0,
              step_id: d.step_id || d.discount_type_id || 5,
              name: d.name || null,
            })),
        })),
      };

      if (isEditMode) {
        await api.updateOrder(orderId, body);
        showToast('Objednávka byla aktualizována', 'success');
        // Otevřené seznamy objednávek (Objednávky, karta zákazníka) se načtou znovu
        window.dispatchEvent(new CustomEvent('orders:refresh'));
      } else {
        const result = await api.createOrder(body);
        showToast('Objednávka byla vytvořena', 'success');
        window.dispatchEvent(new CustomEvent('orders:refresh'));
        // Navigate back to orders list only for new orders
        closeTab(activeTab);
        openTab('orders', 'Objednávky', '/orders', 'OrdersList');
      }
    } catch (err) {
      console.error('Save failed:', err);
      setError(err.message || 'Nepodařilo se uložit objednávku');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    closeTab(activeTab);
  };

  // Odpovědná osoba objednávky = obchodník (v seznamu objednávek je pod tímhle
  // názvem i sloupec). Nabízíme proto jen lidi z vlastní firmy.
  const searchContactPersons = useCallback(async (query) => {
    const res = await api.searchSalespeople(query);
    return (res.data || []).map(p => ({
      id: p.id,
      label: [
        [p.last_name, p.first_name].filter(Boolean).join(' ').trim() || `#${p.id}`,
        p.company_name,
      ].filter(Boolean).join(' — '),
    }));
  }, []);

  if (loadingData) {
    return (
      <Container fluid className="px-4">
        <div className="order-form-container text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Načítání objednávky…</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4">
      <div className="order-form-container">
        {/* Breadcrumb */}
        <div className="breadcrumb-custom">
          <a onClick={() => { closeTab(activeTab); openTab('orders', 'Objednávky', '/orders', 'OrdersList'); }}>
            Objednávky
          </a>
          <i className="bi bi-chevron-right text-muted" style={{ fontSize: '.75rem' }}></i>
          <span>{isEditMode ? `Úprava objednávky ${orderNumber || '#' + orderId}` : 'Nová objednávka'}</span>
          {isEditMode && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                className="ms-auto"
                title="Vytvořit fakturu z této objednávky"
                onClick={() => setShowInvoiceModal(true)}
              >
                <i className="bi bi-receipt me-1"></i>Vytvořit fakturu
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                title="Stáhnout PDF (potvrzení objednávky)"
                onClick={() => window.open(api.getOrderPdfUrl(orderId), '_blank', 'noopener')}
              >
                <i className="bi bi-file-earmark-pdf"></i>
              </Button>
            </>
          )}
        </div>

        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

        {isEditMode && (
          <OrderInvoiceModal
            show={showInvoiceModal}
            onHide={() => setShowInvoiceModal(false)}
            orderId={orderId}
            orderNumber={orderNumber}
            openTab={openTab}
          />
        )}

        {/* Section 1: Header — Zákazník a nastavení */}
        <div className="order-form-card">
          <div className="card-header-custom">
            <h5><i className="bi bi-person-lines-fill me-2"></i>Zákazník a nastavení</h5>
          </div>
          <div className="card-body-custom">
            <Row className="g-3 mb-3">
              <Col md={6}>
                <CompanyAutocomplete
                  label="Zákazník"
                  value={customerId}
                  displayName={customerName}
                  onChange={(company) => {
                    if (company) {
                      setCustomerId(company.id);
                      setCustomerName(company.company_name);
                    } else {
                      setCustomerId('');
                      setCustomerName('');
                    }
                  }}
                  placeholder="Hledat zákazníka…"
                />
              </Col>
              <Col md={6}>
                <CompanyAutocomplete
                  label="Inzerent"
                  value={advertiserId}
                  displayName={advertiserName}
                  onChange={(company) => {
                    if (company) {
                      setAdvertiserId(company.id);
                      setAdvertiserName(company.company_name);
                    } else {
                      setAdvertiserId('');
                      setAdvertiserName('');
                    }
                  }}
                  placeholder="Hledat inzerenta…"
                />
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={4}>
                <Form.Label>Obchodník <span className="text-danger">*</span></Form.Label>
                <SearchableSelect
                  value={responsiblePersonId}
                  selectedLabel={responsiblePersonName}
                  onSearch={searchContactPersons}
                  onChange={(id, opt) => {
                    setResponsiblePersonId(id || '');
                    setResponsiblePersonName(opt ? opt.label : '');
                  }}
                  placeholder="Hledat podle jména nebo e-mailu…"
                  emptyLabel="— vyberte —"
                />
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={4}>
                <Form.Label>Adresa inzerenta</Form.Label>
                <Form.Select
                  value={advertiserAddressId}
                  onChange={(e) => setAdvertiserAddressId(e.target.value)}
                  disabled={!advertiserId || advertiserAddresses.length === 0}
                >
                  <option value="">— vyberte —</option>
                  {adresyInzerenta.map(a => (
                    <option key={a.id} value={a.id}>{popisAdresy(a)}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label>Fakturační adresa</Form.Label>
                <Form.Select
                  value={billingAddressId}
                  onChange={(e) => setBillingAddressId(e.target.value)}
                  disabled={!customerId || customerAddresses.length === 0}
                >
                  <option value="">— vyberte —</option>
                  {adresyZakaznika.map(a => (
                    <option key={a.id} value={a.id}>
                      {popisAdresy(a)}{aresShodaId === a.id ? ' ✓ dle ARES' : ''}
                    </option>
                  ))}
                </Form.Select>
                {aresBlok}
              </Col>
              <Col md={4}>
                <Form.Label>Doručovací adresa</Form.Label>
                <Form.Select
                  value={shippingAddressId}
                  onChange={(e) => setShippingAddressId(e.target.value)}
                  disabled={!customerId || customerAddresses.length === 0}
                >
                  <option value="">— vyberte —</option>
                  {adresyZakaznika.map(a => (
                    <option key={a.id} value={a.id}>{popisAdresy(a)}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={4}>
                <Form.Label>Způsob platby</Form.Label>
                <Form.Select value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
                  {/* Vyřazené způsoby úhrady (mediální barter) se nenabízejí, ale
                      u starší objednávky, která ho má uložený, zůstanou vidět —
                      jinak by uložení přepsalo způsob platby na jiný. */}
                  {enums.payment_types
                    .filter(pt => !pt.hidden || pt.id === paymentType)
                    .map(pt => (
                      <option key={pt.id} value={pt.id}>{pt.label}</option>
                    ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label>Měna</Form.Label>
                <Form.Select value={currency} onChange={(e) => { setCurrency(e.target.value); setExchangeRate(''); }}>
                  <option value="CZK">CZK</option>
                  <option value="EUR">EUR{latestEurRate ? ` (${latestEurRate.toFixed(2)} CZK)` : ''}</option>
                </Form.Select>
              </Col>
              {currency !== 'CZK' && (
                <Col md={2}>
                  <Form.Label>Kurz ({currency}/CZK)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.0001"
                    value={exchangeRate}
                    onChange={(e) => setExchangeRate(e.target.value)}
                    placeholder="např. 25.3400"
                  />
                </Col>
              )}
              <Col md={2}>
                <Form.Label>Zahraniční</Form.Label>
                <Form.Check
                  type="checkbox"
                  checked={abroad}
                  onChange={(e) => setAbroad(e.target.checked)}
                />
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={3}>
                <Form.Label>Číslo objednávky</Form.Label>
                <Form.Control
                  type="text"
                  value={orderNumber || '(bude přiděleno)'}
                  readOnly
                  disabled
                  className="bg-light"
                />
              </Col>
              <Col md={3}>
                <Form.Label>Stav</Form.Label>
                <Form.Select value={state} onChange={(e) => zmenStavObjednavky(e.target.value)}>
                  {enums.booking_states.map(s => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Datum vytvoření</Form.Label>
                <Form.Control
                  type="date"
                  value={dateOfCreation}
                  onChange={(e) => setDateOfCreation(e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Číslo objednávky zákazníka</Form.Label>
                <Form.Control
                  type="text"
                  value={customerOrderNumber}
                  onChange={(e) => setCustomerOrderNumber(e.target.value)}
                  maxLength={255}
                />
              </Col>
              {state === 'RESERVATION' && (
                <Col md={4}>
                  <Form.Label>Platná do</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={endOfReservation}
                    onChange={(e) => setEndOfReservation(e.target.value)}
                  />
                  <div className="form-hint">Datum a čas konce platnosti rezervace</div>
                </Col>
              )}
            </Row>

            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Label>Kontakt pro kontrolu podkladů</Form.Label>
                <div className="sources-suggest">
                  <Form.Control
                    type="text"
                    value={sourcesCheckContacts}
                    onChange={(e) => { setSourcesCheckContacts(e.target.value); setSourcesSuggestOpen(true); }}
                    onFocus={() => setSourcesSuggestOpen(true)}
                    /* blur se zpozdí, aby stihl proběhnout klik na návrh */
                    onBlur={() => setTimeout(() => setSourcesSuggestOpen(false), 150)}
                    placeholder="Vyberte nebo napište kontakt"
                    autoComplete="off"
                    name="sources-check-contact"
                    /* Správci hesel (NordPass, 1Password, LastPass, Bitwarden, Dashlane)
                       si pole s e-mailem pletou s přihlašovacím — tímhle se vypínají. */
                    data-np-ignore="true"
                    data-np-autofill-type="other"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                  />
                  {sourcesSuggestOpen && sourcesSuggestions.length > 0 && (
                    <div className="sources-suggest-list">
                      {sourcesSuggestions.map(c => (
                        <button
                          type="button"
                          key={c.id}
                          className="sources-suggest-item"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => { setSourcesCheckContacts(c.email); setSourcesSuggestOpen(false); }}
                        >
                          <span className="sources-suggest-email">{c.email}</span>
                          <span className="sources-suggest-meta">
                            {[c.first_name, c.last_name].filter(Boolean).join(' ')}
                            {(c.responsibilities || []).length > 0 && ' · ' + c.responsibilities.map(r => r.label).join(', ')}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <Form.Label>Kontakt pro zasílání faktur</Form.Label>
                <Form.Select
                  value={invoiceContactPersonId}
                  onChange={(e) => setInvoiceContactPersonId(e.target.value)}
                  disabled={!customerId || customerContacts.length === 0}
                >
                  <option value="">— fakturační kontakt zákazníka —</option>
                  {kontaktyProFakturu.fakturacni.length > 0 && (
                    <optgroup label="★ Faktury a upomínky">
                      {kontaktyProFakturu.fakturacni.map(c => (
                        <option key={c.id} value={c.id}>
                          {kontaktyProFakturu.jmeno(c)} — {c.email}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {kontaktyProFakturu.ostatni.length > 0 && (
                    <optgroup label="Ostatní kontakty">
                      {kontaktyProFakturu.ostatni.map(c => (
                        <option key={c.id} value={c.id}>
                          {kontaktyProFakturu.jmeno(c)} — {c.email}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </Form.Select>
                <div className="form-hint">Prázdné = fakturační kontakt zákazníka.</div>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Label>Interní poznámka</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                />
              </Col>
            </Row>
          </div>
        </div>

        {/* Section 2: Položky objednávky */}
        <div className="order-form-card">
          <div className="card-header-custom">
            <h5><i className="bi bi-list-check me-2"></i>Položky objednávky</h5>
            <span className="text-muted" style={{ fontSize: '.85rem' }}>{items.length} {items.length === 1 ? 'položka' : items.length < 5 ? 'položky' : 'položek'}</span>
          </div>
          <div className="card-body-custom">
            {items.map((item, idx) => {
              // Compute display number for items without a DB number
              const displayNumber = item.number
                ? item.number
                : orderNumber
                  ? `${orderNumber}/${String(idx + 1).padStart(2, '0')}`
                  : null;
              return (
                <OrderItemCard
                  key={idx}
                  item={{ ...item, _displayNumber: displayNumber }}
                  index={idx}
                  enums={enums}
                  onUpdate={handleItemUpdate}
                  onRemove={handleRemoveItem}
                  onDuplicate={handleDuplicateItem}
                  mediumOptions={allMedia}
                  objednavkaStornovana={state === STAV_STORNO}
                  mena={currency}
                  kurz={currency !== 'CZK' ? parseFloat(exchangeRate) || 0 : 0}
                />
              );
            })}

            <button className="add-item-btn" onClick={handleAddItem}>
              <i className="bi bi-plus-lg me-2"></i>Přidat položku
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="order-form-footer">
          <div className="footer-summary">
            Celkem: <span className="total-amount">{formatPrice(grandTotal)} CZK</span>
            {currency !== 'CZK' && exchangeRate && parseFloat(exchangeRate) > 0 && (
              <span className="total-amount-foreign ms-3">
                ≈ {formatPrice(grandTotal / parseFloat(exchangeRate))} {currency}
              </span>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={handleCancel} disabled={saving}>
              Zrušit
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Ukládání…
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg me-1"></i>
                  {isEditMode ? 'Uložit změny' : 'Vytvořit objednávku'}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Container>
  );
}

export default OrderForm;
